import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { Logger, LoggerService } from './logger.service';
import { UrlResolverService } from './url-resolver-service';
import { UsersService } from "./rest-services/users.service";
import { Auth0Service } from './auth0.service';
import { NTXToastrService } from '@/src/ntx-app/modules/ntx-core/services/ntxToastr.service';

export enum HTTPContentType {
  APPLICATION_JSON = 'application/json',
  APPLICATION_OCTET_STREAM = 'application/octet-stream',
  X_WWW_FORM_URLENCODED = 'application/x-www-form-urlencoded; charset=utf-8',
  TEXT_PLAIN = 'text/plain',
  FORM_DATA = 'multipart/form-data'
}

@Injectable({
  providedIn: 'root'
})
export class HttpSettingInterceptorService implements HttpInterceptor {

  private requestsNum = 0;

  private request: HttpRequest<any>;
  private logger: Logger; components; columnDefs;

  constructor(private urlResolverService: UrlResolverService,
    private usersService: UsersService,
    private loggerFactory: LoggerService,
    private authService: Auth0Service,
    private toastrService: NTXToastrService) {
    this.logger = this.loggerFactory.getLogger("HttpSettingInterceptorService");
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.requestsNum++;

    const headers = this.generateHeaders(request);
    const apiBaseUrl = this.urlResolverService.getBaseApiUrl(request.url === "logout");
    const apiReq = request.clone({
      url: `${apiBaseUrl}/${request.url}`,
      setHeaders: headers
    });

    const localAssetsRegExp = /^[\.]?(\/)?(assets\/)/;
    if (request.url.startsWith('http') || localAssetsRegExp.test(request.url)) {
      this.request = request;
    } else {
      this.request = apiReq;
    }

    return from(next.handle(this.request).toPromise()
      .catch(async (errorResponse: HttpErrorResponse) => {

        if (errorResponse.status === 500) {
          this.toastrService.failure('An unexpected server error occurred.', 'Server Error');
        }

        const isAuthenticated = errorResponse?.status !== 0 && errorResponse?.status !== 401 && errorResponse.message == "undefined";

        if (isAuthenticated) {
          this.logger.error('http error %o', {
            error_type: 'Http Error',
            ...errorResponse
          });

          return throwError(() => new Error(errorResponse.message));
        }
        else if (errorResponse?.url?.includes("globalInfo/access") || errorResponse?.url?.includes("authenticate")) {
            return this.logout();
        }
        else {
          await this.tryReLogin();
          return next.handle(this.request).toPromise();
        }
      }));
  }

  /**
   * Check if the body is formData instance,
   * If yes, remove the Content-Type field (the browser will take care of it)
   */
  generateHeaders(request: HttpRequest<any>) {
    let headers = {
      'Content-Type': HTTPContentType.APPLICATION_JSON,
      Accept: HTTPContentType.APPLICATION_JSON,
    }
    if (request && request.body instanceof FormData) {
      delete headers["Content-Type"]
    }
    if (request && request.responseType == "blob") {
      headers = {
        'Content-Type': HTTPContentType.APPLICATION_JSON,
        Accept: HTTPContentType.APPLICATION_OCTET_STREAM,

      }
    }
    return headers;
  }

  tryReLogin(): Promise<any> {
    this.saveReturnUrl();

    return new Promise(async (resolve, reject) => {
      this.authService.reLogin()
        .then(async res => {
          if (res == null)
            reject(await this.logout());
          else
            resolve(res);
        })
        .catch(async err => {
          reject(await this.logout());
        });
    });
  }

  logout(): Promise<any> {
    this.saveReturnUrl();
    return this.usersService.logout();
  }

  private saveReturnUrl(): void {
    const path = `${window.location.pathname}${window.location.search}`;

    if (!path || path === '/' || path.startsWith('/auth')) {
      return;
    }

    localStorage.setItem('returnUrl', path);
  }
}
